/* Priyanka Musical House — hero slideshow (<pmh-hero-slider>): cross-fade, dots, swipe, autoplay that stops on interaction */
if (!customElements.get('pmh-hero-slider')) {
  customElements.define(
    'pmh-hero-slider',
    class PmhHeroSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.pmh-hero__slide[data-index]'));
        this.dots = Array.from(this.querySelectorAll('[data-pmh-dot]'));
        this.prevButton = this.querySelector('[data-pmh-prev]');
        this.nextButton = this.querySelector('[data-pmh-next]');
        this.pauseButton = this.querySelector('[data-pmh-pause]');
        this.track = this.querySelector('.pmh-hero__track');
        this.index = 0;
        this.timer = null;
        this.hovering = false;
        this.focusWithin = false;
        this.userPaused = false;
        this.delay = (parseInt(this.dataset.speed, 10) || 6) * 1000;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;

        if (this.slides.length < 2) {
          this.syncVideos();
          return;
        }

        this.onPrev = () => this.goTo(this.index - 1, true);
        this.onNext = () => this.goTo(this.index + 1, true);
        this.prevButton?.addEventListener('click', this.onPrev);
        this.nextButton?.addEventListener('click', this.onNext);
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.pmhDot, 10), true));
        });

        this.pauseButton?.addEventListener('click', () => {
          if (this.userPaused || this.userInteracted) {
            this.userPaused = false;
            this.userInteracted = false;
          } else {
            this.userPaused = true;
          }
          this.updatePauseButton();
          this.userPaused ? this.stop() : this.start();
        });

        this.addEventListener('mouseenter', () => {
          this.hovering = true;
          this.stop();
        });
        this.addEventListener('mouseleave', () => {
          this.hovering = false;
          this.start();
        });
        this.addEventListener('focusin', () => {
          this.focusWithin = true;
          this.stop();
        });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) {
            this.focusWithin = false;
            this.start();
          }
        });

        this.addEventListener('keydown', (event) => {
          if (event.target.closest('input, textarea, select')) return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goTo(this.index - 1, true);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goTo(this.index + 1, true);
          }
        });

        this.setupSwipe();

        this.onVisibility = () => (document.hidden ? this.stop() : this.start());
        document.addEventListener('visibilitychange', this.onVisibility);

        this.onReducedMotion = () => {
          this.updatePauseButton();
          this.reducedMotion.matches ? this.stop() : this.start();
          this.syncVideos();
        };
        if (this.reducedMotion.addEventListener) this.reducedMotion.addEventListener('change', this.onReducedMotion);

        // Theme editor support
        this.onBlockSelect = (event) => {
          const i = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
          if (i > -1) {
            this.editorPaused = true;
            this.stop();
            this.goTo(i, false);
          }
        };
        this.onBlockDeselect = () => {
          this.editorPaused = false;
          this.start();
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

        this.updatePauseButton();
        this.syncVideos();
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
        if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        if (this.onReducedMotion && this.reducedMotion.removeEventListener) {
          this.reducedMotion.removeEventListener('change', this.onReducedMotion);
        }
      }

      canAutoplay() {
        return (
          this.autoplayEnabled &&
          !this.userPaused &&
          !this.userInteracted &&
          !this.hovering &&
          !this.focusWithin &&
          !this.editorPaused &&
          !document.hidden &&
          !this.reducedMotion.matches
        );
      }

      start() {
        this.stop();
        if (!this.canAutoplay()) return;
        this.timer = window.setTimeout(() => {
          this.goTo(this.index + 1, false);
          this.start();
        }, this.delay);
      }

      stop() {
        if (this.timer) window.clearTimeout(this.timer);
        this.timer = null;
      }

      goTo(target, fromUser) {
        const count = this.slides.length;
        if (!count) return;
        const next = ((target % count) + count) % count;

        this.slides.forEach((slide, i) => {
          const active = i === next;
          slide.classList.toggle('is-active', active);
          if (active) {
            slide.removeAttribute('inert');
            slide.removeAttribute('aria-hidden');
          } else {
            slide.setAttribute('inert', '');
            slide.setAttribute('aria-hidden', 'true');
          }
        });

        this.dots.forEach((dot, i) => {
          const active = i === next;
          dot.classList.toggle('is-active', active);
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        this.index = next;
        if (this.track) this.track.setAttribute('aria-live', fromUser ? 'polite' : 'off');
        this.syncVideos();
        if (fromUser) {
          // A shopper who swipes or taps a dot is reading: stop rotating for good.
          this.userInteracted = true;
          this.stop();
          this.updatePauseButton();
        }
      }

      syncVideos() {
        this.slides.forEach((slide, i) => {
          slide.querySelectorAll('video').forEach((video) => {
            video.muted = true;
            video.setAttribute('playsinline', '');
            if (i === this.index && !this.reducedMotion.matches) {
              const playing = video.play();
              if (playing && playing.catch) playing.catch(() => {});
            } else {
              video.pause();
            }
          });
        });
      }

      updatePauseButton() {
        if (!this.pauseButton) return;
        const paused = this.userPaused || this.userInteracted || this.reducedMotion.matches;
        this.pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
        this.pauseButton.setAttribute(
          'aria-label',
          paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause
        );
      }

      setupSwipe() {
        if (!this.track) return;
        let startX = 0;
        let startY = 0;
        let tracking = false;
        let swiped = false;

        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          tracking = true;
          swiped = false;
          startX = event.clientX;
          startY = event.clientY;
        });

        const end = (event) => {
          if (!tracking) return;
          tracking = false;
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            swiped = true;
            this.goTo(dx < 0 ? this.index + 1 : this.index - 1, true);
          }
        };

        this.track.addEventListener('pointerup', end);
        this.track.addEventListener('pointercancel', () => {
          tracking = false;
        });

        // Don't follow a link when the user was swiping
        this.track.addEventListener(
          'click',
          (event) => {
            if (swiped) {
              event.preventDefault();
              event.stopPropagation();
              swiped = false;
            }
          },
          true
        );

        // Prevent native image dragging on desktop (breaks mouse swipe)
        this.track.addEventListener('dragstart', (event) => {
          if (event.target.tagName === 'IMG') event.preventDefault();
        });
      }
    }
  );
}
